import React, {useState, useCallback, useEffect} from "react";
import { 
  CreateTable,
  FailMessage,
  Loading,
  FormatValueToDecimal
} from "../common/APICommon";
import { useNavigate } from "react-router-dom";
import { apiUrl } from "../utils/Configuration";
import axios from 'axios';
import {
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Tooltip,
  Legend,
  BarChart,
  CartesianGrid,
  XAxis,
  YAxis,
  Bar,
  LineChart,
  Line,
} from "recharts";
// MUI Icons
import DashboardIcon from '@mui/icons-material/Dashboard';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import AssessmentIcon from '@mui/icons-material/Assessment';
import ArticleIcon from '@mui/icons-material/Article';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import FilterListAltIcon from '@mui/icons-material/FilterListAlt';

const customerChartColors = [
  "#2563eb",
  "#16a34a",
  "#f59e0b",
  "#8b5cf6",
  "#ef4444",
  "#0891b2",
];

export default function Dashboard() {
  const [loading, setLoading] = useState(false);
  const [failMessage, setFailMessage] = useState(""); 
  const getCurrency = JSON.parse(localStorage.getItem("currency") || "{}");

  const today = new Date();
  const _dateTo = today.toISOString().split("T")[0];

  const oneMonthBefore = new Date(today);
  oneMonthBefore.setMonth(oneMonthBefore.getMonth() - 1);

  const [dateFrom, setDateFrom] = useState( oneMonthBefore.toISOString().split("T")[0] );
  const [dateTo, setDateTo] = useState(_dateTo);

  const [tempDateFrom, setTempDateFrom] = useState(oneMonthBefore.toISOString().split("T")[0]);
  const [tempDateTo, setTempDateTo] = useState(_dateTo);

  const applyFilter = () => {
    if(dateFrom !== tempDateFrom){
      setDateFrom(tempDateFrom)
    }

    if(dateTo !== tempDateTo){
      setDateTo(tempDateTo)
    }
  }
  
//#region Table data
  const [currentPage, setCurrentPage] = useState(1);
  const [data, setData] = useState([]);

  const headers = [
    'DETAILS', 
    'REFERENCE', 
    'DEBIT', 
    'CREDIT', 
  ]
  const accessors = [
    'details', 
    'reference', 
    'debit', 
    'credit', 
  ]

  const actions = [
  ];

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);

      const data = {
        dateFrom: dateFrom,
        dateTo: dateTo,
      }

      const response = await axios.post(`${apiUrl}/api/Report/dashboard`, data, {
        headers: { 'Content-Type': 'application/json'}
      })
        
      setData(response.data)
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  }, [dateFrom, dateTo]);

  console.log({data})

  useEffect(() => {
      fetchData();
  }, [fetchData])
//#endregion



  const formatValue = (val) => {
    const symbol = getCurrency?.symbol || "";
    return `${symbol}${FormatValueToDecimal(val || 0)}`;
  };

  const stats = [
    { 
      label: "Total Receivables", 
      amount: formatValue(data?.totalReceivables), 
      icon: <TrendingUpIcon sx={{ fontSize: '1.5rem' }} />, 
      bgColor: "bg-emerald-50", 
      iconColor: "text-emerald-600",
      borderColor: "border-emerald-100",
      textColor: "text-green-500",
    },
    { 
      label: "Total Payables", 
      amount: formatValue(data?.totalPayables), 
      icon: <TrendingDownIcon sx={{ fontSize: '1.5rem' }} />, 
      bgColor: "bg-rose-50", 
      iconColor: "text-rose-600",
      borderColor: "border-rose-100",
      textColor: `${data?.totalPayables > 0 ? "text-red-500" : "text-gray-600"}`,
    },
    { 
      label: "Net Cash Flow", 
      amount: `${data?.canetCashFlowsh < 0 ? "-" : ""}${formatValue(Math.abs(data?.netCashFlow))}`, 
      icon: <AccountBalanceWalletIcon sx={{ fontSize: '1.5rem' }} />, 
      bgColor: "bg-sky-50", 
      iconColor: "text-sky-600",
      borderColor: "border-sky-100",
      textColor: `${data?.netCashFlow < 0 ? "text-red-500" 
        : data?.netCashFlow > 0 ? "text-green-500" 
        : "text-gray-600"}`,
    },
    { 
      label: "Pending Journals", 
      amount: data?.pendingJournals || 0, 
      icon: <AssessmentIcon sx={{ fontSize: '1.5rem' }} />, 
      bgColor: "bg-amber-50", 
      iconColor: "text-amber-600",
      borderColor: "border-amber-100",
      textColor: "text-gray-600",
    },
  ];

  const navigate = useNavigate();
  const newJournalEntryHandleClick = () => {
    navigate("/journals");
  }

  return (
    <>
      <div className="p-6 bg-gray-50 min-h-screen">
        {/* Header Section */}
        <div className="flex flex-col md:flex-row md:items-center justify-between mb-8 gap-4">
          <div>
            <h2 className="text-3xl font-bold text-gray-800 flex items-center gap-2">
              <DashboardIcon fontSize="large" className="text-theme-color" />
              Dashboard
            </h2>
            <p className="text-gray-500 mt-1">Real-time accounting overview and ledger activities.</p>
          </div>
          <div className="flex gap-3">
            <button onClick={()=>setFailMessage("This feature is under maintenance")} className="hidden items-center gap-2 bg-white border border-gray-300 px-4 py-2 rounded-lg text-sm font-semibold hover:bg-gray-50 transition shadow-sm">
              <ArticleIcon fontSize="small" /> Reports
            </button>
            <button onClick={newJournalEntryHandleClick} className="flex items-center gap-2 bg-theme-color text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-theme-color-h transition shadow-md">
              <AddCircleOutlineIcon fontSize="small" /> New Journal Entry
            </button>
          </div>
        </div>

        {/* Date Filter */}
        <div className="flex flex-col sm:flex-row sm:items-end gap-4 mb-6 p-4 bg-white border border-gray-200 rounded-xl shadow-sm">
          <div className="flex flex-col gap-1.5">
            <label
              htmlFor="dateFrom"
              className="text-xs font-semibold text-gray-600"
            >
              Date From
            </label>
            <input
              id="dateFrom"
              type="date"
              value={tempDateFrom}
              onChange={(e) => setTempDateFrom(e.target.value)}
              className="w-full sm:w-48 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 outline-none transition focus:border-theme-color focus:ring-2 focus:ring-theme-color/20"
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <label
              htmlFor="dateTo"
              className="text-xs font-semibold text-gray-600"
            >
              Date To
            </label>
            <input
              id="dateTo"
              type="date"
              value={tempDateTo}
              onChange={(e) => setTempDateTo(e.target.value)}
              min={dateFrom}
              className="w-full sm:w-48 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 outline-none transition focus:border-theme-color focus:ring-2 focus:ring-theme-color/20"
            />
          </div>

          <button
            type="button"
            onClick={() => applyFilter()}
            className="inline-flex items-center justify-center gap-2 rounded-lg bg-theme-color px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-theme-color-h"
          >
            <FilterListAltIcon fontSize="small" />
            Apply Filter
          </button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {stats.map((stat, index) => (
            <div 
              key={index} 
              className="relative overflow-hidden bg-white p-6 rounded-2xl border border-gray-100 shadow-sm hover:shadow-lg transition-all duration-300 group"
            >
              {/* Decorative Background Accent */}
              <div className={`absolute top-0 right-0 w-24 h-24 -mr-8 -mt-8 rounded-full opacity-20 transition-transform group-hover:scale-110 ${stat.bgColor}`} />

              <div className="relative flex flex-col h-full">
                {/* Icon Header */}
                <div className={`w-12 h-12 flex items-center justify-center rounded-xl mb-4 ${stat.bgColor} ${stat.iconColor} ring-4 ring-white shadow-sm`}>
                  {stat.icon}
                </div>

                {/* Content */}
                <div className="space-y-1">
                  <p className="text-sm font-semibold text-gray-500 uppercase tracking-wider">
                    {stat.label}
                  </p>
                  <div className="flex items-baseline">
                    <h3 className={`text-2xl font-extrabold ${stat.textColor} tracking-tight`}>
                      {stat.amount}
                    </h3>
                  </div>
                </div>

                {/* Bottom Accent Line */}
                {/* <div className={`absolute bottom-0 left-0 h-1 w-0 group-hover:w-full transition-all duration-500 ${stat.bgColor.replace('50', '500')}`} /> */}
              </div>
            </div>
          ))}
        </div>
        
        <div className="flex flex-row gap-2 mb-8">
          <div className="w-full rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
            <h3 className="text-theme-color font-bold py-2">
              Top Customers by Sales
            </h3>

            <p className="text-sm text-gray-500 mb-4">
              Customer sales distribution for the selected period
            </p>

            {data?.customerSalesChart?.length > 0 ? (
              <>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={data.customerSalesChart}
                      dataKey="totalSales"
                      nameKey="customerName"
                      cx="50%"
                      cy="50%"
                      innerRadius={65}
                      outerRadius={100}
                      paddingAngle={3}
                    >
                      {data.customerSalesChart.map((entry, index) => (
                        <Cell
                          key={entry.customerId}
                          fill={
                            customerChartColors[
                              index % customerChartColors.length
                            ]
                          }
                        />
                      ))}
                    </Pie>

                    <Tooltip
                      formatter={(value) =>
                        `₱${Number(value).toLocaleString("en-PH", {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}`
                      }
                    />

                    <Legend verticalAlign="bottom" height={45} />
                  </PieChart>
                </ResponsiveContainer>

                <div className="mt-3 border-t border-gray-100 pt-4">
                  <p className="text-sm text-gray-500">
                    Total Sales
                  </p>
                  <p className="text-2xl font-bold text-gray-800">
                    ₱
                    {data.customerSalesChart
                      .reduce(
                        (total, customer) =>
                          total + Number(customer.totalSales || 0),
                        0
                      )
                      .toLocaleString("en-PH", {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                  </p>
                </div>
              </>
            ) : (
              <div className="flex h-[300px] items-center justify-center text-sm text-gray-500">
                No customer sales data available for this period.
              </div>
            )}
          </div>

          <div className="w-full rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
            <h3 className="text-theme-color font-bold py-2">
              Unpaid Customers
            </h3>

            <p className="text-sm text-gray-500 mb-4">
              Pending customer payments for the selected period
            </p>

            {data?.unpaidCustomers?.length > 0 ? (
              <>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart
                    data={[...data.unpaidCustomers]
                      .sort(
                        (a, b) =>
                          Number(b.totalUnpaid) - Number(a.totalUnpaid)
                      )
                      .slice(0, 6)}
                    layout="vertical"
                    margin={{ top: 5, right: 20, left: 10, bottom: 5 }}
                  >
                    <CartesianGrid
                      strokeDasharray="3 3"
                      horizontal={false}
                    />

                    <XAxis
                      type="number"
                      tickFormatter={(value) =>
                        `₱${Number(value).toLocaleString("en-PH")}`
                      }
                    />

                    <YAxis
                      type="category"
                      dataKey="customerName"
                      width={120}
                      tick={{ fontSize: 11 }}
                    />

                    <Tooltip
                      formatter={(value, name, props) => [
                        `₱${Number(value).toLocaleString("en-PH", {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}`,
                        "Unpaid Amount",
                      ]}
                      labelFormatter={(label) => `Customer: ${label}`}
                    />

                    <Bar
                      dataKey="totalUnpaid"
                      name="Unpaid Amount"
                      fill="#ef4444"
                      radius={[0, 6, 6, 0]}
                      barSize={22}
                    />
                  </BarChart>
                </ResponsiveContainer>

                <div className="mt-3 border-t border-gray-100 pt-4">
                  <p className="text-sm text-gray-500">
                    Total Pending Payments
                  </p>

                  <p className="text-2xl font-bold text-gray-800">
                    ₱
                    {data.unpaidCustomers
                      .reduce(
                        (total, customer) =>
                          total + Number(customer.totalUnpaid || 0),
                        0
                      )
                      .toLocaleString("en-PH", {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                  </p>
                </div>
              </>
            ) : (
              <div className="flex h-[300px] items-center justify-center text-sm text-gray-500">
                No unpaid customer data available for this period.
              </div>
            )}
          </div>
        </div>

        <div className="flex flex-col lg:flex-row gap-2 mb-8">
          {/* Net Customer Receipts - Line Chart */}
          <div className="w-full min-w-0 rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
            <h3 className="text-theme-color font-bold py-2">
              Net Customer Receipts
            </h3>

            <p className="text-sm text-gray-500 mb-4">
              Total money received from customers, net of voids and adjustments
            </p>

            {data?.netCustomerReceipts?.length > 0 ? (
              <>
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart
                    data={[...data.netCustomerReceipts]
                      .sort((a, b) => Number(b.amount) - Number(a.amount))
                      .slice(0, 6)}
                    margin={{ top: 10, right: 20, left: 10, bottom: 35 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />

                    <XAxis
                      dataKey="customerName"
                      interval={0}
                      angle={-25}
                      textAnchor="end"
                      height={65}
                      tick={{ fontSize: 10 }}
                    />

                    <YAxis
                      tickFormatter={(value) =>
                        `₱${Number(value).toLocaleString("en-PH")}`
                      }
                      tick={{ fontSize: 10 }}
                    />

                    <Tooltip
                      formatter={(value) => [
                        `₱${Number(value).toLocaleString("en-PH", {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}`,
                        "Net Receipts",
                      ]}
                      labelFormatter={(label) => `Customer: ${label}`}
                    />

                    <Line
                      type="monotone"
                      dataKey="amount"
                      name="Net Receipts"
                      stroke="#0d9488"
                      strokeWidth={3}
                      dot={{ r: 4, fill: "#0d9488" }}
                      activeDot={{ r: 7 }}
                    />
                  </LineChart>
                </ResponsiveContainer>

                <div className="mt-3 border-t border-gray-100 pt-4">
                  <p className="text-sm text-gray-500">
                    Total Net Customer Receipts
                  </p>

                  <p className="text-2xl font-bold text-gray-800">
                    ₱
                    {data.netCustomerReceipts
                      .reduce(
                        (total, customer) =>
                          total + Number(customer.amount || 0),
                        0
                      )
                      .toLocaleString("en-PH", {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                  </p>
                </div>
              </>
            ) : (
              <div className="flex h-[300px] items-center justify-center text-sm text-gray-500">
                No customer receipts.
              </div>
            )}
          </div>

          {/* Net Supplier Payments - Column Chart */}
          <div className="w-full min-w-0 rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
            <h3 className="text-theme-color font-bold py-2">
              Net Supplier Payments
            </h3>

            <p className="text-sm text-gray-500 mb-4">
              Total paid to suppliers, net of voids and adjustments
            </p>

            {data?.netSupplierPayments?.length > 0 ? (
              <>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart
                    data={[...data.netSupplierPayments]
                      .sort((a, b) => Number(b.amount) - Number(a.amount))
                      .slice(0, 6)}
                    margin={{ top: 10, right: 10, left: 10, bottom: 35 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />

                    <XAxis
                      dataKey="vendorName"
                      interval={0}
                      angle={-25}
                      textAnchor="end"
                      height={65}
                      tick={{ fontSize: 10 }}
                    />

                    <YAxis
                      tickFormatter={(value) =>
                        `₱${Number(value).toLocaleString("en-PH")}`
                      }
                      tick={{ fontSize: 10 }}
                    />

                    <Tooltip
                      formatter={(value) => [
                        `₱${Number(value).toLocaleString("en-PH", {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}`,
                        "Net Supplier Payments",
                      ]}
                      labelFormatter={(label) => `Supplier: ${label}`}
                    />

                    <Bar
                      dataKey="amount"
                      name="Net Supplier Payments"
                      fill="#6366f1"
                      radius={[6, 6, 0, 0]}
                      maxBarSize={45}
                    />
                  </BarChart>
                </ResponsiveContainer>

                <div className="mt-3 border-t border-gray-100 pt-4">
                  <p className="text-sm text-gray-500">
                    Total Net Supplier Payments
                  </p>

                  <p className="text-2xl font-bold text-gray-800">
                    ₱
                    {data.netSupplierPayments
                      .reduce(
                        (total, vendor) =>
                          total + Number(vendor.amount || 0),
                        0
                      )
                      .toLocaleString("en-PH", {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                  </p>
                </div>
              </>
            ) : (
              <div className="flex h-[300px] items-center justify-center text-sm text-gray-500">
                No supplier payments.
              </div>
            )}
          </div>
        </div>

        <div className="flex flex-row gap-2 mb-8">
          <div className="w-full rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
            <h3 className="text-theme-color font-bold py-2">
              Recent Transactions
            </h3>

            <CreateTable
              tableName={"Dashboard Recent Transactions"}
              headers={headers}
              accessors={accessors}
              data={data?.glAccountTransactions ?? []}
              actions={actions}
              currentPage={currentPage}
              setCurrentPage={setCurrentPage}
              itemsPerPage={10}
              totalItems={data?.glAccountTransactions?.length}
            />
          </div>

          <div className="w-full">
          </div>
        </div>

      </div>
      
      <div className="fixed top-5 right-5 z-50 flex flex-col space-y-2">
        <FailMessage message={failMessage} onClose={() => setFailMessage("")} />
      </div>

      { loading && <Loading />}
    </>
  );
}